import { PriceType } from "./priceType"

const toInteger = value => parseInt(value || 0, 10)
const toNumber = value => parseFloat(value || 0)

export const isAlternativePriceType = type =>
    type === PriceType.MINIMUN ||
    type === PriceType.MULTIPLE ||
    type === PriceType.TAKEMOREPAYLESS

const isFacilityProgramPrice = price => price?.programaFidelidade === true

const getPricesByFacilityProgram = (prices, customerHasFacilityProgram, predicate) => {
    const pricesWithoutFacilityProgram = prices?.filter(price => predicate(price) && !isFacilityProgramPrice(price)) || []

    if (!customerHasFacilityProgram)
        return pricesWithoutFacilityProgram

    const pricesWithFacilityProgram = prices?.filter(price => predicate(price) && isFacilityProgramPrice(price)) || []
    return pricesWithFacilityProgram.length > 0 ? pricesWithFacilityProgram : pricesWithoutFacilityProgram
}

const getNormalPrice = (prices, customerHasFacilityProgram) => {
    const [normalPrice] = getPricesByFacilityProgram(prices, customerHasFacilityProgram, price => price.tipo === PriceType.NORMAL)
    return normalPrice || prices?.find(price => price.tipo === PriceType.NORMAL)
}

const getFallbackNormalPrice = prices => {
    const fallbackPrice = prices?.find(price => price.tipo === PriceType.TAKEMOREPAYLESS) || prices?.[0]

    if (!fallbackPrice)
        return null

    const regularPrice = fallbackPrice.precoDe || fallbackPrice.precoPor || 0

    return {
        ...fallbackPrice,
        tipo: PriceType.NORMAL,
        precoDe: regularPrice,
        precoPor: regularPrice,
        percentualDesconto: 0
    }
}

const getAlternativePrices = (prices, customerHasFacilityProgram) =>
    getPricesByFacilityProgram(prices, customerHasFacilityProgram, price => isAlternativePriceType(price.tipo))

const getPriceId = (price, product, type) =>
    `${product?.id || "product"}-${type}-${price?.promotionId || price?.id || 0}`

const mapAlternativePrice = price => ({
    priceId: price.promotionId || price.id,
    promotionId: price.promotionId,
    priceOf: price.precoDe,
    priceBy: price.precoPor,
    discount: price.percentualDesconto,
    type: price.tipo,
    facilityProgram: price.programaFidelidade,
    minimunQuantity: price.quantidadeMinima,
    multipleQuantity: price.quantidadeMultipla,
    takeQuantity: price.leve,
    payQuantity: price.pague
})

const sortTakeMorePayLessPrices = (a, b) => {
    const priceByA = toNumber(a.precoPor || a.priceBy)
    const priceByB = toNumber(b.precoPor || b.priceBy)
    const takeQuantityA = toInteger(a.leve || a.takeQuantity)
    const takeQuantityB = toInteger(b.leve || b.takeQuantity)
    const payQuantityA = toInteger(a.pague || a.payQuantity)
    const payQuantityB = toInteger(b.pague || b.payQuantity)

    if (priceByA !== priceByB)
        return priceByA - priceByB

    if (takeQuantityA !== takeQuantityB)
        return takeQuantityB - takeQuantityA

    return payQuantityA - payQuantityB
}

const sortAlternativePrices = prices => {
    if (prices.some(price => price.type === PriceType.TAKEMOREPAYLESS))
        return prices.sort(sortTakeMorePayLessPrices)

    if (prices.some(price => price.type === PriceType.MINIMUN))
        return prices.sort((a, b) => a.minimunQuantity - b.minimunQuantity)

    if (prices.some(price => price.type === PriceType.MULTIPLE))
        return prices.sort((a, b) => a.multipleQuantity - b.multipleQuantity)

    return prices
}

// Produto sem nenhum preço de venda maior que zero não pode ir para a cesta (aparece como indisponível).
export const temPrecoDeVenda = prices => (prices || []).some(price => toNumber(price?.precoPor) > 0)

export const getPromotionsSubtotal = promotions =>
    (promotions || []).reduce((acc, promotion) => acc + toNumber(promotion.priceOf) * promotion.promotionQuantity, 0)

export const getPromotionsTotal = promotions =>
    (promotions || []).reduce((acc, promotion) => acc + toNumber(promotion.priceBy) * promotion.promotionQuantity, 0)

export const getIdealInfoPrice = (prices, customerHasFacilityProgram) => {
    let priceOf = 0
    let priceBy = 0
    let discount = 0
    let hasFacilityProgramInPriceBy = false
    let alternativePrices = []
    let lowestAlternativePrice = null

    const normalPrice = getNormalPrice(prices, customerHasFacilityProgram)

    if (normalPrice) {
        priceOf = normalPrice?.precoDe || 0
        priceBy = normalPrice?.precoPor || 0
        discount = normalPrice?.percentualDesconto || 0
        hasFacilityProgramInPriceBy = normalPrice?.programaFidelidade === true
    }

    alternativePrices = getAlternativePrices(prices, customerHasFacilityProgram)?.map(mapAlternativePrice)

    if (alternativePrices && alternativePrices.length > 0) {
        alternativePrices = sortAlternativePrices(alternativePrices)
        lowestAlternativePrice = alternativePrices?.reduce((prev, current) => {
            return toNumber(prev.priceBy) < toNumber(current.priceBy) ? prev : current
        })

        if (!normalPrice) {
            priceOf = lowestAlternativePrice?.priceOf || priceOf
            priceBy = lowestAlternativePrice?.priceBy || priceBy
            discount = lowestAlternativePrice?.discount || discount
        }
    }

    return {
        priceOf,
        priceBy,
        discount,
        hasFacilityProgramInPriceBy,
        alternativePrices,
        lowestAlternativePrice
    }
}

export const getExtractProductsCart = (products, customerHasFacilityProgram) => {
    const promotions = []
    let productsId = [];
    if (products && products.length > 0) {
        products.forEach(product => {
            if (!productsId.some(id => id === product.id))
                productsId.push(product.id)

            const { quantidade: quantity, precos: prices } = product
            const orderedQuantity = quantity
            let rest = orderedQuantity

            const alternativePrices = getAlternativePrices(prices, customerHasFacilityProgram)

            if (alternativePrices?.length > 0) {
                const hasTakeMorePayLessPrice = alternativePrices.some(p => p.tipo === PriceType.TAKEMOREPAYLESS)
                const hasMinimunPrice = alternativePrices.some(p => p.tipo === PriceType.MINIMUN)

                if (hasTakeMorePayLessPrice) {
                    let takeMorePayLessPrices = alternativePrices
                        .filter(p => p.tipo === PriceType.TAKEMOREPAYLESS && toInteger(p.leve) > 0 && toInteger(p.leve) <= rest)
                        .sort(sortTakeMorePayLessPrices)

                    while (takeMorePayLessPrices.length > 0) {
                        const mainTakeMorePayLessPrice = takeMorePayLessPrices[0]
                        const takeQuantity = toInteger(mainTakeMorePayLessPrice.leve)
                        const restOperation = rest % takeQuantity
                        const promotionQuantity = rest - restOperation

                        if (promotionQuantity > 0) {
                            promotions.push({
                                priceId: getPriceId(mainTakeMorePayLessPrice, product, PriceType.TAKEMOREPAYLESS),
                                productId: product.id,
                                product,
                                promotionQuantity,
                                totalQuantity: quantity,
                                priceOf: mainTakeMorePayLessPrice.precoDe,
                                priceBy: mainTakeMorePayLessPrice.precoPor,
                                discount: mainTakeMorePayLessPrice.percentualDesconto,
                                hasFacilityProgramInPriceBy: mainTakeMorePayLessPrice.programaFidelidade,
                                type: mainTakeMorePayLessPrice.tipo,
                                takeQuantity: mainTakeMorePayLessPrice.leve,
                                payQuantity: mainTakeMorePayLessPrice.pague,
                                promotionId: mainTakeMorePayLessPrice.promotionId,
                            })
                        }

                        takeMorePayLessPrices = takeMorePayLessPrices.filter(p => toInteger(p.leve) > 0 && toInteger(p.leve) <= restOperation)
                        rest = restOperation
                    }
                } else if (hasMinimunPrice) {
                    const minimunPrices = alternativePrices
                        .filter(p => p.tipo === PriceType.MINIMUN && p.quantidadeMinima > 0 && p.quantidadeMinima <= orderedQuantity)
                        .sort((a, b) => a.quantidadeMinima - b.quantidadeMinima)
                    if (minimunPrices && minimunPrices.length > 0) {
                        const mainMinimunPrice = minimunPrices[minimunPrices.length - 1]
                        rest = 0
                        promotions.push({
                            priceId: getPriceId(mainMinimunPrice, product, PriceType.MINIMUN),
                            productId: product.id,
                            product,
                            promotionQuantity: orderedQuantity,
                            totalQuantity: quantity,
                            priceOf: mainMinimunPrice.precoDe,
                            priceBy: mainMinimunPrice.precoPor,
                            discount: mainMinimunPrice.percentualDesconto,
                            hasFacilityProgramInPriceBy: mainMinimunPrice.programaFidelidade,
                            type: mainMinimunPrice.tipo,
                            minimunQuantity: mainMinimunPrice.quantidadeMinima,
                        })
                    }

                } else {
                    let multiplePrices = alternativePrices
                        .filter(p => p.tipo === PriceType.MULTIPLE && p.quantidadeMultipla > 0 && p.quantidadeMultipla <= orderedQuantity)
                        .sort((a, b) => a.quantidadeMultipla - b.quantidadeMultipla)
                    if (multiplePrices && multiplePrices.length > 0) {
                        while (multiplePrices.length > 0) {
                            const mainMultiplePrice = multiplePrices[multiplePrices.length - 1]
                            const restOperation = rest % mainMultiplePrice.quantidadeMultipla
                            promotions.push({
                                priceId: getPriceId(mainMultiplePrice, product, PriceType.MULTIPLE),
                                productId: product.id,
                                product,
                                promotionQuantity: rest - restOperation,
                                totalQuantity: quantity,
                                priceOf: mainMultiplePrice.precoDe,
                                priceBy: mainMultiplePrice.precoPor,
                                discount: mainMultiplePrice.percentualDesconto,
                                hasFacilityProgramInPriceBy: mainMultiplePrice.programaFidelidade,
                                type: mainMultiplePrice.tipo,
                                multipleQuantity: mainMultiplePrice.quantidadeMultipla
                            })
                            multiplePrices = multiplePrices.filter(p => p.quantidadeMultipla > 0 && p.quantidadeMultipla <= restOperation)
                            rest = restOperation
                        }
                    }
                }
            }

            if (rest > 0) {
                let normalPrice = getNormalPrice(prices, customerHasFacilityProgram) || getFallbackNormalPrice(prices)

                if (!normalPrice)
                    return

                promotions.push({
                    priceId: getPriceId(normalPrice, product, PriceType.NORMAL),
                    productId: product.id,
                    product,
                    promotionQuantity: rest,
                    totalQuantity: quantity,
                    priceOf: normalPrice.precoDe,
                    priceBy: normalPrice.precoPor,
                    discount: normalPrice.percentualDesconto,
                    hasFacilityProgramInPriceBy: normalPrice.programaFidelidade,
                    type: PriceType.NORMAL
                })

                rest = 0
            }
        })
    }

    return {
        promotions,
        distinctProductsIdCount: productsId.length
    }
}
